import React, { useEffect, useRef, useState } from "react";
import { streamSSE, http, API } from "../lib/api";
import MarkdownLite from "../components/MarkdownLite";
import { Sparkle, FileMagnifyingGlass, Upload, User, Ranking } from "@phosphor-icons/react";

const SAMPLE_CV = `Mohammed Al-Saud
Senior Payroll Professional · Riyadh, KSA
m.alsaud@example.com · +966 50 111 2233

SUMMARY
11 years end-to-end payroll management in large enterprises, expert in
Payroll and HCM leadership, Saudi GOSI/WPS compliance, and multi-entity
shared services. Led 3 ERP cutovers.

EXPERIENCE
• Payroll Manager — Saudi Industrial Group (2019–present)
  – Owns monthly payroll for 8,200 employees across 4 entities.
  – Implemented HCM Payroll, cut close cycle from 7 to 3 days.
• Senior Payroll Specialist — STC (2014–2019)

EDUCATION
B.Sc. Accounting, KAU 2013. CPP certified 2017.

SKILLS
HCM Payroll, Payroll Compliance, GOSI, WPS, SAP, Excel/Power Query,
End of Service, Saudi Labor Law, Power BI`;

function candidateToCvText(c) {
  if (!c) return "";
  // Prefer previously-saved plain resume text if it exists.
  if (typeof c.resumeText === "string" && c.resumeText.trim()) return c.resumeText;
  // If resume is a plain string (older data), use it directly.
  if (typeof c.resume === "string" && c.resume.trim()) return c.resume;

  const lines = [
    c.name || "",
    [c.position, c.location].filter(Boolean).join(" · "),
    [c.email, c.phone].filter(Boolean).join(" · "),
  ].filter(Boolean);

  if (c.profileSummary) lines.push("", "SUMMARY", c.profileSummary);

  // Structured resume object: { workExperience: [...], education: [...] }
  if (c.resume && typeof c.resume === "object") {
    const we = Array.isArray(c.resume.workExperience) ? c.resume.workExperience : [];
    if (we.length) {
      lines.push("", "EXPERIENCE");
      we.forEach((r) => {
        lines.push(`• ${[r.title, r.company].filter(Boolean).join(" — ")}${r.period ? ` (${r.period})` : ""}`);
        if (r.summary) lines.push(`  ${r.summary}`);
      });
    }
    const edu = Array.isArray(c.resume.education) ? c.resume.education : [];
    if (edu.length) {
      lines.push("", "EDUCATION");
      edu.forEach((e) => {
        lines.push(`• ${[e.degree, e.school].filter(Boolean).join(" — ")}${e.year ? ` (${e.year})` : ""}`);
      });
    }
  } else if (typeof c.experience === "string" && c.experience) {
    lines.push("", "EXPERIENCE", c.experience);
  }

  if (Array.isArray(c.skills) && c.skills.length) lines.push("", "SKILLS", c.skills.join(", "));
  return lines.join("\n");
}

export default function CVEvaluation() {
  const [mode, setMode] = useState("single"); // "single" | "bulk"
  const [jobTitle, setJobTitle] = useState("Payroll Manager");
  const [cvText, setCvText] = useState("");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [reqsSource, setReqsSource] = useState(null);
  const [reqsLoading, setReqsLoading] = useState(true);
  const [selectedReq, setSelectedReq] = useState("");
  const [customMode, setCustomMode] = useState(false);

  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [source, setSource] = useState(null); // {kind: 'candidate'|'file'|'sample'|'manual', label}
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [savingToProfile, setSavingToProfile] = useState(false);
  const [savedToProfileId, setSavedToProfileId] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    http.get("/fusion/requisitions").then((r) => {
      if (!alive) return;
      setReqs(r.data?.items || []);
      setReqsSource(r.data?.source || null);
      setReqsLoading(false);
    }).catch(() => alive && setReqsLoading(false));
    http.get("/candidates").then((r) => {
      if (!alive) return;
      setCandidates(r.data || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const pickReq = (reqNumber) => {
    setSelectedReq(reqNumber);
    if (!reqNumber) return;
    const r = reqs.find((x) => x.RequisitionNumber === reqNumber);
    if (r?.Title) setJobTitle(r.Title);
  };

  const pickCandidate = async (idStr) => {
    setSelectedCandidateId(idStr);
    setUploadError(null);
    setSavedToProfileId(null);
    if (!idStr) {
      setSource(null);
      return;
    }
    // Always fetch the freshest candidate on pick — the list may be stale if
    // the user just uploaded a new CV via the drawer or portal.
    let c = candidates.find((x) => String(x.id) === String(idStr));
    try {
      const r = await http.get(`/candidates/${idStr}`);
      c = r.data;
      setCandidates((prev) => prev.map((x) => (String(x.id) === String(idStr) ? r.data : x)));
    } catch (_e) { /* fall back to cached copy */ }
    if (c) {
      setCvText(candidateToCvText(c));
      setSource({ kind: "candidate", label: `${c.name} (#${c.id})` });
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const token = localStorage.getItem("vaku_access_token");
      const res = await fetch(`${API}/cv/extract`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      setCvText(data.text);
      setSource({ kind: "file", label: `${data.filename} (${data.chars.toLocaleString()} chars)`, filename: data.filename });
      setSavedToProfileId(null);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadSample = () => {
    setCvText(SAMPLE_CV);
    setSource({ kind: "sample", label: "Sample CV" });
    setSelectedCandidateId("");
    setUploadError(null);
    setSavedToProfileId(null);
  };

  const clearAll = () => {
    setCvText("");
    setSource(null);
    setSelectedCandidateId("");
    setUploadError(null);
    setSavedToProfileId(null);
  };

  const saveToProfile = async () => {
    if (!selectedCandidateId || !cvText.trim()) return;
    setSavingToProfile(true);
    try {
      const payload = {
        resumeText: cvText,
        resumeSource: source?.filename || (source?.kind === "sample" ? "Sample CV" : "Manual entry"),
        resumeUpdatedAt: new Date().toISOString(),
      };
      const { data: updated } = await http.patch(`/candidates/${selectedCandidateId}`, payload);
      // Refresh the local candidate cache so re-picking shows the saved text.
      setCandidates((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setSavedToProfileId(selectedCandidateId);
    } catch (e) {
      setUploadError(e.response?.data?.detail || e.message || "Failed to save CV to profile");
    } finally {
      setSavingToProfile(false);
    }
  };

  const selectedCandidate = candidates.find((c) => String(c.id) === String(selectedCandidateId));
  const canSaveToProfile = !!selectedCandidate && cvText.trim().length > 0 && source?.kind !== "candidate";

  const run = async () => {
    setOutput("");
    setStreaming(true);
    await streamSSE(
      "/ai/cv-evaluation",
      { jobTitle, cvText },
      {
        onDelta: (_d, full) => setOutput(full),
        onDone: () => setStreaming(false),
        onError: () => setStreaming(false),
      }
    );
  };

  return (
    <div className="space-y-5" data-testid="cv-evaluation-page">
      <div>
        <div className="label-eyebrow">Applicant Tracking System</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">CV Evaluation (ATS)</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Score and summarize a CV against any role using Claude Sonnet 4.5.
        </p>
      </div>

      <div className="inline-flex rounded-md border border-slate-200 bg-white p-1" data-testid="cv-mode-toggle">
        <button
          className={`px-3 py-1.5 text-xs font-medium rounded ${mode === "single" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          onClick={() => setMode("single")}
          data-testid="cv-mode-single"
        >Single CV</button>
        <button
          className={`px-3 py-1.5 text-xs font-medium rounded ${mode === "bulk" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          onClick={() => setMode("bulk")}
          data-testid="cv-mode-bulk"
        >Bulk vs. Requisition</button>
      </div>

      {mode === "bulk" && <BulkEvaluation reqs={reqs} reqsLoading={reqsLoading} />}
      {mode === "single" && (      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label-eyebrow">Target Role</label>
              <button
                type="button"
                data-testid="cv-toggle-custom"
                className="text-[11px] text-[#002FA7] hover:underline"
                onClick={() => { setCustomMode(!customMode); if (!customMode) setSelectedReq(""); }}
              >
                {customMode ? "← Pick from requisitions" : "Enter custom role instead"}
              </button>
            </div>
            {!customMode ? (
              <>
                <select
                  data-testid="cv-requisition"
                  className="input"
                  value={selectedReq}
                  onChange={(e) => pickReq(e.target.value)}
                  disabled={reqsLoading}
                >
                  <option value="">
                    {reqsLoading ? "Loading requisitions…" : reqs.length === 0 ? "No requisitions available" : "— Select a requisition —"}
                  </option>
                  {reqs.map((r) => (
                    <option key={r.RequisitionNumber} value={r.RequisitionNumber}>
                      {r.RequisitionNumber} — {r.Title}
                    </option>
                  ))}
                </select>
                {reqsSource && reqs.length > 0 && (
                  <div className="text-[11px] text-emerald-700 mt-1">
                    {reqs.length} requisition{reqs.length === 1 ? "" : "s"} · scoring against: <strong>{jobTitle}</strong>
                  </div>
                )}
                {reqs.length === 0 && !reqsLoading && (
                  <div className="text-[11px] text-amber-700 mt-1">
                    No requisitions available. Create one in <em>Requisitions</em> and mark it Open, or switch to &quot;custom role&quot;.
                  </div>
                )}
              </>
            ) : (
              <input
                data-testid="cv-job-title"
                className="input"
                placeholder="e.g. Payroll Manager"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            )}
          </div>

          {/* CV source picker */}
          <div className="pt-2 border-t">
            <label className="label-eyebrow block mb-2">CV Source</label>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
                  <User size={11} /> Pick from your candidate list
                </div>
                <select
                  data-testid="cv-candidate-picker"
                  className="input"
                  value={selectedCandidateId}
                  onChange={(e) => pickCandidate(e.target.value)}
                >
                  <option value="">— Select an existing candidate —</option>
                  {candidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      #{c.id} · {c.name} · {c.position || "—"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col justify-end">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
                  <Upload size={11} /> Or attach file
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  data-testid="cv-file-input"
                  accept=".pdf,.docx,.txt,.md"
                  onChange={handleFile}
                  className="hidden"
                />
                <button
                  type="button"
                  data-testid="cv-attach-btn"
                  className="btn btn-outline whitespace-nowrap"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload size={12} weight="bold" />
                  {uploading ? "Reading…" : "Attach CV (PDF / DOCX)"}
                </button>
              </div>
            </div>
            {source && (
              <div className="text-[11px] text-[#002FA7] bg-indigo-50 rounded px-2 py-1 mt-2 flex items-center justify-between" data-testid="cv-source-chip">
                <span>Loaded from: <strong>{source.label}</strong></span>
                <button type="button" className="text-slate-500 hover:text-slate-800 underline" onClick={clearAll} data-testid="cv-clear-btn">
                  Clear
                </button>
              </div>
            )}
            {canSaveToProfile && (
              <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-center justify-between gap-2" data-testid="cv-save-to-profile-prompt">
                <span>
                  Save this CV to <strong>{selectedCandidate.name}</strong> (#{selectedCandidate.id}) for future evaluations?
                </span>
                <button
                  type="button"
                  data-testid="cv-save-to-profile-btn"
                  className="btn btn-primary !py-1 !px-2 !text-[11px] whitespace-nowrap"
                  onClick={saveToProfile}
                  disabled={savingToProfile}
                >
                  {savingToProfile ? "Saving…" : "Save to profile"}
                </button>
              </div>
            )}
            {savedToProfileId && String(savedToProfileId) === String(selectedCandidateId) && (
              <div className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-800" data-testid="cv-save-success">
                ✓ CV saved to {selectedCandidate?.name}&apos;s profile. Re-selecting this candidate will now show the same text.
              </div>
            )}
            {uploadError && (
              <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2" data-testid="cv-upload-error">
                {uploadError}
              </div>
            )}
          </div>

          <label className="label-eyebrow">CV Text</label>
          <textarea
            data-testid="cv-text"
            className="input"
            style={{ minHeight: 280 }}
            placeholder="Pick a candidate, attach a CV file, load the sample, or paste CV text here…"
            value={cvText}
            onChange={(e) => { setCvText(e.target.value); if (source) setSource({ kind: "manual", label: "Manually edited" }); }}
          />
          <div className="flex gap-2 pt-1">
            <button
              data-testid="cv-evaluate-btn"
              className="btn btn-primary"
              onClick={run}
              disabled={streaming || !cvText.trim()}
            >
              <Sparkle size={14} weight="fill" />
              {streaming ? "Evaluating…" : "Evaluate CV"}
            </button>
            <button
              className="btn btn-outline"
              onClick={loadSample}
              data-testid="cv-load-sample"
            >
              Load Sample
            </button>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileMagnifyingGlass size={18} weight="fill" className="text-[#002FA7]" />
            <h2 className="font-display font-bold text-lg">Evaluation Result</h2>
          </div>
          <div className="border bg-[#fafbfd] rounded-sm p-4 min-h-[340px]">
            {output ? (
              <>
                <MarkdownLite text={output} />
                {streaming && <span className="cursor-blink" />}
              </>
            ) : (
              <div className="text-slate-400 text-sm text-center py-12">
                Paste a CV and click Evaluate to see scoring + recommendation.
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function BulkEvaluation({ reqs, reqsLoading }) {
  const [selectedReq, setSelectedReq] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const pickReq = (num) => {
    setSelectedReq(num);
    const r = reqs.find((x) => x.RequisitionNumber === num);
    if (r) {
      setJobTitle(r.Title || "");
      setJobDescription(r.Description || "");
    }
  };

  const run = async () => {
    setError(null);
    if (!jobTitle.trim()) {
      setError("Pick a requisition (or type a role title) first.");
      return;
    }
    setRunning(true);
    setResults(null);
    try {
      const r = await http.post("/ai/cv-evaluation/bulk", {
        jobTitle: jobTitle.trim(),
        jobDescription,
        requisitionNumber: selectedReq || null,
        statusFilter: statusFilter === "All" ? null : statusFilter,
      });
      setResults(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setRunning(false); }
  };

  const recPill = (rec) => {
    const cls = rec === "Advance" ? "bg-emerald-100 text-emerald-800"
      : rec === "Reject" ? "bg-red-100 text-red-800"
      : rec === "Hold" ? "bg-amber-100 text-amber-800"
      : "bg-slate-100 text-slate-700";
    return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>{rec || "—"}</span>;
  };

  return (
    <div className="space-y-4" data-testid="cv-bulk-page">
      <div className="card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Ranking size={18} weight="fill" className="text-[#002FA7]" />
          <h2 className="font-display font-bold text-lg">Score all candidates against a requisition</h2>
        </div>
        <p className="text-sm text-slate-500">
          Pick a requisition and Vaku will score every candidate&apos;s stored CV against the job description, producing a ranked shortlist.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="label-eyebrow block mb-1">Requisition</label>
            <select
              data-testid="bulk-req"
              className="input"
              value={selectedReq}
              onChange={(e) => pickReq(e.target.value)}
              disabled={reqsLoading}
            >
              <option value="">
                {reqsLoading ? "Loading requisitions…" : reqs.length === 0 ? "No requisitions available" : "— Select a requisition —"}
              </option>
              {reqs.map((r) => (
                <option key={r.RequisitionNumber} value={r.RequisitionNumber}>
                  {r.RequisitionNumber} — {r.Title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Filter candidates by status</label>
            <select
              data-testid="bulk-status"
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {["All", "New", "Screening", "Interview", "Offer", "Hired", "Rejected"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Job title</label>
          <input
            data-testid="bulk-title"
            className="input"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Senior Payroll Analyst"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Job description (auto-filled from requisition — edit if needed)</label>
          <textarea
            data-testid="bulk-jd"
            className="input"
            rows={4}
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Paste the job description here — the AI uses this as the scoring rubric."
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="bulk-run"
            className="btn btn-primary"
            onClick={run}
            disabled={running || !jobTitle.trim()}
          >
            <Sparkle size={14} weight="fill" />
            {running ? "Scoring candidates… this can take up to a minute" : "Score all candidates"}
          </button>
          {error && <span className="text-xs text-red-700" data-testid="bulk-error">{error}</span>}
        </div>
      </div>

      {results && (
        <div className="card overflow-hidden" data-testid="bulk-results">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Ranked Results</div>
              <div className="text-sm text-slate-500">
                {results.scored} candidates scored against <strong>{results.jobTitle}</strong>{results.requisitionNumber ? ` (Req #${results.requisitionNumber})` : ""}
              </div>
            </div>
            <span className="pill">{results.scored}</span>
          </div>
          {results.warning && (
            <div className="mx-4 mb-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">{results.warning}</div>
          )}
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Candidate</th>
                <th>Position</th>
                <th>Status</th>
                <th style={{ width: 80 }}>Score</th>
                <th style={{ width: 100 }}>Recommend.</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {results.results.map((r, i) => (
                <React.Fragment key={r.candidateId}>
                  <tr data-testid={`bulk-row-${r.candidateId}`}>
                    <td className="font-mono text-xs text-slate-400">{i + 1}</td>
                    <td className="font-medium">
                      {r.name}
                      <div className="text-[11px] text-slate-500 font-normal">{r.email}</div>
                    </td>
                    <td>{r.position || "—"}</td>
                    <td><span className="text-xs">{r.status || "—"}</span></td>
                    <td>
                      <div className={`inline-block font-mono font-bold ${r.score >= 80 ? "text-emerald-700" : r.score >= 60 ? "text-slate-700" : "text-amber-700"}`}>
                        {r.score}
                      </div>
                    </td>
                    <td>{recPill(r.recommendation)}</td>
                    <td>
                      <button
                        className="text-xs text-[#002FA7] hover:underline"
                        onClick={() => setExpandedId(expandedId === r.candidateId ? null : r.candidateId)}
                        data-testid={`bulk-expand-${r.candidateId}`}
                      >
                        {expandedId === r.candidateId ? "Hide" : "Details"}
                      </button>
                    </td>
                  </tr>
                  {expandedId === r.candidateId && (
                    <tr className="bg-slate-50">
                      <td></td>
                      <td colSpan={6} className="text-xs">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                          <div>
                            <div className="label-eyebrow !text-[10px] mb-1 text-emerald-700">Strengths</div>
                            <ul className="list-disc ml-4 space-y-0.5">
                              {(r.strengths || []).map((s, idx) => <li key={idx}>{s}</li>)}
                              {(!r.strengths || r.strengths.length === 0) && <li className="text-slate-400">—</li>}
                            </ul>
                          </div>
                          <div>
                            <div className="label-eyebrow !text-[10px] mb-1 text-red-700">Gaps</div>
                            <ul className="list-disc ml-4 space-y-0.5">
                              {(r.gaps || []).map((g, idx) => <li key={idx}>{g}</li>)}
                              {(!r.gaps || r.gaps.length === 0) && <li className="text-slate-400">—</li>}
                            </ul>
                          </div>
                          {r.reasoning && (
                            <div className="md:col-span-2 text-slate-600 italic">&ldquo;{r.reasoning}&rdquo;</div>
                          )}
                          {r.error && (
                            <div className="md:col-span-2 text-red-700">Error: {r.error}</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {results.results.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-6">No candidates matched.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
